import { ResponsiveLine } from "@nivo/line";
import { useTheme, Box, Typography, alpha, Paper } from "@mui/material";
import { tokens } from "../theme";
import { motion } from "framer-motion";

// Improved Empty State
const EmptyState = ({ colors }) => (
  <Paper
    elevation={3}
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(4px)",
      background: alpha(colors.primary[400], 0.7),
      borderRadius: 2,
      p: 3,
      textAlign: "center",
    }}
  >
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Typography
        variant="h5"
        color={colors.grey[100]}
        gutterBottom
        fontWeight="bold"
      >
        No Attendance Data Yet
      </Typography>
      <Typography variant="body1" color={colors.grey[300]}>
        Driver attendance overview will appear here once trips are recorded.
      </Typography>
      <Typography
        variant="body2"
        color={colors.greenAccent[500]}
        sx={{ mt: 2 }}
      >
        Start recording trips to see detailed attendance patterns.
      </Typography>
    </motion.div>
  </Paper>
);

// Improved Tooltip Component
const CustomTooltip = ({ point }) => {
  const data = point.data;
  return (
    <Paper
      sx={{
        background: point.serieColor,
        padding: "12px",
        borderRadius: "4px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      <Typography variant="subtitle2" sx={{ color: "#fff", mb: 1 }}>
        {data.x}
      </Typography>
      <Typography variant="body2" sx={{ color: "#fff" }}>
        Hours: <strong>{data.y}</strong>
      </Typography>
      {data.tripCount && (
        <Typography variant="body2" sx={{ color: "#fff", mt: 0.5 }}>
          Trips: <strong>{data.tripCount}</strong>
        </Typography>
      )}
    </Paper>
  );
};

const AttendanceChart = ({ data = [], isLoading = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Fallback default data if none is provided.
  const defaultData = [
    {
      id: "Hours Worked",
      color: colors.greenAccent[500],
      data: [
        { x: "Mon", y: 8, tripCount: 3 },
        { x: "Tue", y: 7.5, tripCount: 2 },
        { x: "Wed", y: 8.2, tripCount: 4 },
        { x: "Thu", y: 7.8, tripCount: 3 },
        { x: "Fri", y: 7.9, tripCount: 3 },
        { x: "Sat", y: 6.5, tripCount: 2 },
        { x: "Sun", y: 0, tripCount: 0 },
      ],
    },
  ];

  // Use provided data or default.
  const chartData = data.length ? data : defaultData;
  const hasNoData =
    !data.length || data.every((series) => series.data.length === 0);

  if (hasNoData && !isLoading) {
    return <EmptyState colors={colors} />;
  }

  // Calculate some statistics for the summary (using first series as an example)
  const statsData = chartData[0]?.data || [];
  const totalHours = statsData.reduce(
    (acc, d) => acc + parseFloat(d.y || 0),
    0
  );
  const averageHours = statsData.length
    ? (totalHours / statsData.length).toFixed(1)
    : 0;
  const totalTrips = statsData.reduce(
    (acc, d) => acc + (parseInt(d.tripCount) || 0),
    0
  );
  const averageTrips = statsData.length
    ? (totalTrips / statsData.length).toFixed(1)
    : 0;

  return (
    <Box sx={{ width: "100%", height: "100%", p: 2 }}>
      {/* Chart Heading */}
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{ color: colors.grey[100] }}
      >
        Driver Attendance Overview
      </Typography>

      {/* Responsive Chart Container */}
      <Box sx={{ height: "300px", width: "100%", mb: 3 }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{ height: "100%" }}
        >
          <ResponsiveLine
            data={chartData}
            theme={{
              background: colors.primary[400],
              textColor: colors.grey[100],
              fontSize: 12,
              axis: {
                domain: {
                  line: { stroke: colors.grey[100], strokeWidth: 1 },
                },
                legend: {
                  text: {
                    fill: colors.grey[100],
                    fontSize: 12,
                    fontWeight: 600,
                  },
                },
                ticks: {
                  line: { stroke: colors.grey[100], strokeWidth: 1 },
                  text: {
                    fill: colors.grey[100],
                    fontSize: 11,
                    fontWeight: 500,
                  },
                },
              },
              legends: {
                text: { fill: colors.grey[100], fontSize: 11, fontWeight: 500 },
              },
              grid: { line: { stroke: alpha(colors.grey[100], 0.15) } },
              crosshair: {
                line: {
                  stroke: colors.greenAccent[500],
                  strokeWidth: 1,
                  strokeOpacity: 0.5,
                },
              },
            }}
            // Increased margins to avoid cut-off elements.
            margin={{ top: 60, right: 160, bottom: 60, left: 80 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: 0,
              max: "auto",
              stacked: false,
              reverse: false,
            }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Date",
              legendOffset: 40,
              legendPosition: "middle",
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Hours Worked",
              legendOffset: -60,
              legendPosition: "middle",
            }}
            enableGridX={false}
            enableGridY={true}
            pointSize={10}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            enableArea={true}
            areaOpacity={0.25}
            useMesh={true}
            enableSlices="x"
            crosshairType="cross"
            motionConfig="wobbly"
            tooltip={({ point }) => <CustomTooltip point={point} />}
            legends={[
              {
                anchor: "bottom-right",
                direction: "column",
                justify: false,
                translateX: 130,
                translateY: 0,
                itemsSpacing: 8,
                itemDirection: "left-to-right",
                itemWidth: 100,
                itemHeight: 24,
                itemOpacity: 0.85,
                symbolSize: 12,
                symbolShape: "circle",
                symbolBorderColor: alpha(colors.grey[100], 0.5),
                effects: [
                  {
                    on: "hover",
                    style: {
                      itemBackground: alpha(colors.primary[500], 0.4),
                      itemOpacity: 1,
                      itemBorderRadius: 4,
                    },
                  },
                ],
              },
            ]}
            annotations={[
              {
                type: "line",
                match: { key: "y", value: 8 },
                style: {
                  stroke: colors.redAccent[500],
                  strokeWidth: 1,
                  strokeDasharray: "6 6",
                },
              },
            ]}
            // Layer to add an average line
            layers={[
              "grid",
              "markers",
              "areas",
              "crosshair",
              "lines",
              "points",
              "slices",
              "axes",
              "legends",
              ({ xScale, yScale, width }) => {
                if (!statsData.length) return null;
                const averageY =
                  statsData.reduce((acc, d) => acc + d.y, 0) / statsData.length;
                const y = yScale(averageY);
                return (
                  <g>
                    <line
                      x1={0}
                      x2={width}
                      y1={y}
                      y2={y}
                      stroke={colors.greenAccent[500]}
                      strokeWidth={1}
                      strokeDasharray="4 4"
                    />
                    <text
                      x={width - 5}
                      y={y - 5}
                      fill={colors.greenAccent[500]}
                      fontSize={11}
                      textAnchor="end"
                    >
                      Average: {averageY.toFixed(1)}h
                    </text>
                  </g>
                );
              },
            ]}
          />
        </motion.div>
      </Box>

      {/* Enhanced Stats Summary */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {[
          { label: "Average Hours/Day", value: averageHours + "h" },
          { label: "Total Hours", value: totalHours.toFixed(1) + "h" },
          { label: "Total Trips", value: totalTrips },
          { label: "Average Trips/Day", value: averageTrips },
        ].map((stat) => (
          <Paper
            key={stat.label}
            elevation={3}
            sx={{
              bgcolor: alpha(colors.primary[400], 0.6),
              p: 2,
              borderRadius: 2,
              minWidth: 140,
              textAlign: "center",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.grey[300], mb: 0.5 }}
            >
              {stat.label}
            </Typography>
            <Typography variant="h6" sx={{ color: colors.greenAccent[500] }}>
              {stat.value}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default AttendanceChart;
